import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, Mail, ChevronDown } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { CopyButton } from './CopyButton';
import type { ParsedEmail } from '../../types/results';

type CardTone = 'blue' | 'emerald' | 'pink' | 'violet' | 'amber' | 'cyan';

const ACTIVE_TONE: Record<CardTone, string> = {
  blue:    'border-blue-400/60 ring-blue-400/40 shadow-[0_0_32px_rgba(37,99,235,0.35)]',
  emerald: 'border-emerald-400/60 ring-emerald-400/40 shadow-[0_0_32px_rgba(16,185,129,0.35)]',
  pink:    'border-pink-300/70 ring-pink-300/45 shadow-[0_0_32px_rgba(244,143,177,0.4)]',
  violet:  'border-violet-400/60 ring-violet-400/40 shadow-[0_0_32px_rgba(139,92,246,0.35)]',
  amber:   'border-amber-400/60 ring-amber-400/40 shadow-[0_0_32px_rgba(245,158,11,0.35)]',
  cyan:    'border-cyan-400/60 ring-cyan-400/40 shadow-[0_0_32px_rgba(34,211,238,0.35)]',
};

const NUMBER_BADGE: Record<CardTone, string> = {
  blue:    'bg-blue-500/15 text-blue-300',
  emerald: 'bg-emerald-500/15 text-emerald-300',
  pink:    'bg-pink-400/15 text-pink-200',
  violet:  'bg-violet-500/15 text-violet-300',
  amber:   'bg-amber-500/15 text-amber-300',
  cyan:    'bg-cyan-500/15 text-cyan-300',
};

interface EmailCardProps {
  email: ParsedEmail;
  index: number;
  /** DOM id so the calendar sidebar can scroll to this card */
  cardId?: string;
  /** Glow/highlight this card (hovered/clicked on the calendar) */
  isActive?: boolean;
  /** Start expanded by default (set when user clicks on calendar) */
  startExpanded?: boolean;
  /** Emit hover on this card so the calendar can reflect it */
  onHoverChange?: (hovered: boolean) => void;
  /** Accent color for active glow + number badge (default blue) */
  tone?: CardTone;
}

export function EmailCard({ email, index, cardId, isActive, startExpanded, onHoverChange, tone = 'blue' }: EmailCardProps) {
  const [isExpanded, setIsExpanded] = useState(!!startExpanded);

  return (
    <motion.div
      id={cardId}
      onMouseEnter={() => onHoverChange?.(true)}
      onMouseLeave={() => onHoverChange?.(false)}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className={`rounded-2xl bg-slate-900/50 backdrop-blur-xl overflow-hidden transition-all duration-300 ring-1 scroll-mt-24 ${
        isActive
          ? ACTIVE_TONE[tone]
          : 'border border-slate-700/50 ring-transparent'
      }`}
    >
      {/* Header - clickable to toggle */}
      <button
        type="button"
        onClick={() => setIsExpanded((prev) => !prev)}
        className="flex items-center justify-between w-full p-4 text-left hover:bg-slate-800/30 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold ${NUMBER_BADGE[tone]}`}>
            {email.number}
          </div>
          <div>
            <p className="font-medium text-slate-200 text-sm truncate max-w-xs sm:max-w-md">
              {email.subject}
            </p>
            <div className="flex items-center gap-1.5 text-xs text-slate-500 mt-0.5">
              <Calendar className="w-3 h-3" />
              {email.sendDay}
            </div>
          </div>
        </div>
        <motion.div
          animate={{ rotate: isExpanded ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <ChevronDown className="w-5 h-5 text-slate-400" />
        </motion.div>
      </button>

      {/* Collapsible content */}
      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            {/* Actions */}
            <div className="flex items-center gap-2 px-4 pb-2">
              <CopyButton text={email.subject} variant="small" label="Subject" />
              <CopyButton text={email.body} variant="small" label="Body" />
              <CopyButton text={email.rawText} variant="icon" label="Copy All" />
            </div>

            {/* Subject */}
            <div className="mx-4 py-3 bg-slate-800/30 rounded-lg px-3 border border-slate-700/30">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-slate-500 shrink-0" />
                <p className="font-medium text-slate-200 text-sm">{email.subject}</p>
              </div>
            </div>

            {/* Body */}
            <div className="p-4">
              <div className="prose prose-invert prose-sm max-w-none prose-headings:text-slate-100 prose-p:text-slate-300 prose-strong:text-slate-200 prose-li:text-slate-300">
                <ReactMarkdown>{email.body}</ReactMarkdown>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
